/* THE FUNDS PAGE — a flat PORTFOLIO TABLE (Tier-2 D3, Ale ruling
 * 2026-09-05: "Funds list as a portfolio table — ledger columns, doors";
 * audit REPORT §9, mock mocks/funds-portfolio-table.png; flat-table idiom
 * from the fund Record flat v2/v4 — header group tints, one grid,
 * two-digit days).
 *
 * One line per fund: FUND (name · form · jurisdiction) · TO BOOK · TO
 * DECIDE · BREAKS · OWED (with the optional split muted) · AWAITING
 * SIGN-OFF · CHANGED SINCE · CONTACT. EVERY FIGURE IS A DOOR: it opens
 * its queue scoped to the fund (?fund=<id>); the name opens the fund's
 * Overview; a fund with no contact stored carries the chip that opens the
 * capture. A fund with nothing on its record reads as a sentence, not as
 * a row of zeros.
 *
 * Data path: identity from GET /api/funds, EVERY FIGURE from the bulk
 * ledger GET /api/funds/ledger (B1 "one ledger, read everywhere") — the
 * page adds, sorts and filters what the ledger served and re-derives
 * nothing. The rows, the sort and the day-0 rule are proven in
 * fundsTable.ts / tests/unit/fundsTable.test.mjs. */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import { fetchFundLedgers } from './ledger'
import type { FundLedger } from './ledgerWords'
import {
  COLUMNS,
  DEFAULT_SORT,
  buildRows,
  changedSinceLabel,
  day0Words,
  figureOf,
  filterRows,
  footOf,
  footWords,
  metaWords,
  nextSort,
  optionalWords,
  sortRows,
  type Column,
  type FundIdentity,
  type FundRow,
  type Sort,
} from './fundsTable'
import { getRegister, syncRegisterNow, useRegisterEnabled } from './register/api'
import './funds.css'
import ContextFundsPage from './navigation/ContextFundsPage'
import { useContextualNavigation } from './navigation/useRead'
import { ErrorState } from './state'

/** The queue door: the queue, scoped to this fund. */
function queueHref(queue: string, fundId: string): string {
  return `/v2/workbench/${queue}?fund=${encodeURIComponent(fundId)}`
}

/** The changed-since door: the fund's documents, scoped to the fund. */
function changedHref(fundId: string): string {
  return `/v2/traces?fund=${encodeURIComponent(fundId)}`
}

/** Where a missing contact is captured (the same door Today opens). */
function addContactHref(fundId: string): string {
  return `/v2/workbench/chase-request?fund=${encodeURIComponent(fundId)}&contact=add`
}

const CELL_CLASS: Partial<Record<Column['key'], string>> = {
  to_book: 'book',
  to_decide: 'decide',
  breaks: 'brk',
  owed: 'owed',
}

/** One figure cell: the ledger's number, as a door when it carries work
 *  and as a quiet zero when it does not. "—" while the ledger is silent. */
function FigureCell({ row, col }: { row: FundRow; col: Column }) {
  const n = figureOf(row, col.key)
  const href =
    col.queue && row.ledger
      ? queueHref(col.queue, row.id)
      : col.key === 'changed' && row.ledger
        ? changedHref(row.id)
        : null
  const optional = col.key === 'owed' ? optionalWords(row) : null
  const testId = `fpt-${col.key}-${row.id}`
  if (n === null)
    return (
      <td className={`num zero ${CELL_CLASS[col.key] ?? ''}`} data-testid={testId}>
        —
      </td>
    )
  return (
    <td className={`num ${n ? (CELL_CLASS[col.key] ?? '') : 'zero'}`} data-testid={testId}>
      {n && href ? (
        <Link className="fpt-door" to={href}>
          {n}
        </Link>
      ) : (
        n
      )}
      {optional ? <span className="fpt-optional"> · {optional}</span> : null}
    </td>
  )
}

function ContactCell({ row }: { row: FundRow }) {
  return (
    <td data-testid={`fpt-contact-${row.id}`}>
      {row.contact ? (
        row.contact
      ) : (
        <Link className="fpt-addcontact" to={addContactHref(row.id)} data-testid="fpt-add-contact">
          no contact stored › add
        </Link>
      )}
    </td>
  )
}

function Row({ row }: { row: FundRow }) {
  const work = COLUMNS.filter((c) => c.group === 'work')
  const changed = COLUMNS.find((c) => c.key === 'changed') as Column
  return (
    <tr data-testid="fpt-row" data-fund={row.id}>
      <td>
        <div className="fpt-fund">
          <Link className="nm" to={`/v2/funds/${row.id}`} title={row.name}>
            {row.name}
          </Link>
          <span className="meta">{metaWords(row)}</span>
        </div>
      </td>
      {row.day0 && row.ledger ? (
        <td className="fpt-day0" colSpan={work.length} data-testid="fpt-day0">
          {row.ledger.to_book > 0 ? (
            <>
              nothing on the record yet ·{' '}
              <Link className="fpt-door" to={queueHref('record-keeping', row.id)}>
                {row.ledger.to_book}
              </Link>{' '}
              to book
            </>
          ) : (
            day0Words(row.ledger)
          )}
        </td>
      ) : (
        work.map((c) => <FigureCell key={c.key} row={row} col={c} />)
      )}
      <FigureCell row={row} col={changed} />
      <ContactCell row={row} />
    </tr>
  )
}

export default function FundsPage() {
  const capability = useContextualNavigation()
  if (capability.error)
    return (
      <main className="detail">
        <ErrorState what="fund navigation" error={capability.error} />
      </main>
    )
  if (capability.loading)
    return (
      <main className="detail" role="status">
        Loading funds…
      </main>
    )
  return capability.value?.enabled ? <ContextFundsPage /> : <LegacyFundsPage />
}

function LegacyFundsPage() {
  /* Three states: null = loading, 'error' = the read failed (never a
   * silently blank page — atlas review 2026-07-27), array = loaded. */
  const [funds, setFunds] = useState<FundIdentity[] | 'error' | null>(null)
  const [ledgers, setLedgers] = useState<Record<string, FundLedger>>({})
  const [since, setSince] = useState<string | null>(null)
  const [find, setFind] = useState('')
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT)
  // Demo-v2 W1: with the register on, the page carries the sync stamp +
  // manual refresh — the officer's proof the external agreement register
  // and the product agree, and the impatient path when it lags.
  const registerOn = useRegisterEnabled()
  const [syncedAt, setSyncedAt] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)

  const load = useCallback(() => {
    setFunds(null)
    apiFetch<FundIdentity[]>('/api/funds')
      .then(setFunds)
      .catch(() => setFunds('error'))
    fetchFundLedgers()
      .then((l) => {
        setLedgers(Object.fromEntries(l.funds.map((f) => [f.fund_id, f])))
        setSince(l.since)
      })
      .catch(() => setLedgers({}))
  }, [])
  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    if (registerOn)
      getRegister()
        .then((r) => setSyncedAt(r.last_sync?.synced_at ?? null))
        .catch(() => {})
  }, [registerOn])
  const refreshRegister = useCallback(() => {
    setSyncing(true)
    syncRegisterNow()
      .then((r) => {
        if (r.report) setSyncedAt(r.report.synced_at)
        load()
      })
      .catch(() => {})
      .finally(() => setSyncing(false))
  }, [load])

  const rows = useMemo(
    () => (Array.isArray(funds) ? buildRows(funds, ledgers) : []),
    [funds, ledgers]
  )
  const shown = useMemo(() => sortRows(filterRows(rows, find), sort), [rows, find, sort])
  const foot = useMemo(() => footOf(shown), [shown])

  const label = (c: Column) => (c.key === 'changed' ? changedSinceLabel(since) : c.label)

  return (
    <main className="detail">
      <div className="dhead">
        <h1>Funds</h1>
        {registerOn ? (
          <>
            <span
              className="mono"
              style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginLeft: 'auto' }}
              data-testid="register-synced-stamp"
            >
              {syncedAt
                ? `register synced · ${new Date(syncedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}`
                : 'register not synced yet'}
            </span>
            <button
              className="vbtn"
              onClick={refreshRegister}
              disabled={syncing}
              data-testid="register-refresh"
            >
              {syncing ? 'Refreshing…' : 'Refresh register'}
            </button>
          </>
        ) : null}
      </div>
      <div className="dbody fpt" data-testid="funds-table-page">
        <div className="fpt-tools">
          <input
            className="fpt-find"
            type="search"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="find — name, form, jurisdiction, contact"
            aria-label="find a fund by name, form, jurisdiction or contact"
            data-testid="fpt-find"
          />
          <span className="fpt-count" data-testid="fpt-shown">
            {find.trim() ? `${shown.length} of ${rows.length} funds` : `${rows.length} funds`}
          </span>
        </div>

        {funds === 'error' ? (
          <div className="fpt-empty">
            <div style={{ color: 'var(--gap)' }}>The fund list could not be loaded.</div>
            <button className="vbtn" style={{ marginTop: 8 }} onClick={load}>
              Try again
            </button>
          </div>
        ) : (
          <div className="fpt-wrap">
            <table className="fpt-t" data-testid="funds-table">
              <colgroup>
                <col style={{ width: 'auto' }} />
                <col style={{ width: '78px' }} />
                <col style={{ width: '88px' }} />
                <col style={{ width: '74px' }} />
                <col style={{ width: '128px' }} />
                <col style={{ width: '142px' }} />
                <col style={{ width: '204px' }} />
                <col style={{ width: '192px' }} />
              </colgroup>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`g-${c.group}${c.num ? ' num' : ''}`}
                      aria-sort={
                        sort.key === c.key
                          ? sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                      data-testid={`fpt-h-${c.key}`}
                    >
                      <button type="button" onClick={() => setSort((s) => nextSort(s, c.key))}>
                        {label(c)}
                        {sort.key === c.key ? (
                          <span className="fpt-caret" aria-hidden="true">
                            {sort.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <Row key={r.id} row={r} />
                ))}
              </tbody>
            </table>
            {funds === null ? (
              <div className="fpt-empty">Loading funds…</div>
            ) : shown.length === 0 ? (
              <div className="fpt-empty" data-testid="fpt-none">
                {rows.length === 0 ? 'No funds on record yet.' : 'No fund matches that.'}
              </div>
            ) : null}
            <div className="fpt-foot" data-testid="fpt-foot">
              {footWords(foot)}
            </div>
          </div>
        )}
      </div>
    </main>
  )
}
